import { useEffect } from 'react';
import './index.css';
import './styles/theme.css';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import DefaultLayout from './layout/DefaultLayout.jsx';
import { ToastProvider } from './context/ToastContext.js';
import NoPermissionFound from './components/NoPermissionFound/NoPermissionFound.js';
import { FilterProvider } from './context/FIlterContext.js';
import { EmployeFilterProvider } from './context/EmployeFilterContext.js';
import { PrimeReactProvider } from 'primereact/api';
import RequestAction from './pages/Features/RequestAction/RequestAction.jsx';
import ForgotPassword from './pages/Auth/ForgotPassword/ForgotPassword.jsx';
import Resetpass from './pages/Auth/ResetPassword/Resetpass.jsx';
import InsightsLogin from './pages/Auth/Login/InsightsLogin.jsx';
import { ThemeProvider } from './context/ThemeContext.js';
import useAuthStore from './Store/authStore.js';
import { decryptData } from './utils/encrypt.js';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const loginSuccess = useAuthStore((state) => state.loginSuccess);
  const myPermission = useAuthStore((state) => state.myPermission);
  const noPermission = useAuthStore((state) => state.noPermission);
  const { getMyPermissions, setLoginSuccess } = useAuthStore();
  let userCode = decryptData(sessionStorage.getItem("user_code"));

  useEffect(() => {
    if (userCode !== null && userCode !== undefined && !loginSuccess) {
      setLoginSuccess(true)
      getMyPermissions("");
    } else if ((location.pathname !== "/sign-in" && location.pathname !== "/reset-password" && location.pathname !== "/sign-up" && location.pathname !== "/request-action") && !location.pathname.includes('atlassian.net') && !loginSuccess) {
      setLoginSuccess(false)
      sessionStorage.clear()
      navigate("/sign-in");
    }
    if ((location?.pathname === "/no-permission" || location?.pathname === "/sign-in" || location?.pathname === "/sign-up" || location?.pathname === "/reset-password") && loginSuccess && !noPermission) {
      navigate("/employee-dashboard", { replace: true });
    } else if (location?.pathname !== "/no-permission" && loginSuccess && noPermission) {
      navigate("/no-permission", { replace: true });
    }
  }, [location, loginSuccess, noPermission, navigate]);

  useEffect(() => {
    const behavior = localStorage.getItem('filterBehavior') || 'overlay';
    document.body.setAttribute('data-filter-behavior', behavior);

    let lastDrawerOpen = null;
    const checkDrawerState = () => {
      const hasOpenDrawer = document.querySelector('.sk-drawer-panel.open') !== null;
      if (hasOpenDrawer !== lastDrawerOpen) {
        lastDrawerOpen = hasOpenDrawer;
        if (hasOpenDrawer) {
          document.body.classList.add('sk-drawer-open');
        } else {
          document.body.classList.remove('sk-drawer-open');
        }
      }
    };

    const processButtons = () => {
      checkDrawerState();
      const buttons = document.querySelectorAll('button, .btn, .sk-btn, .btn-primary, .btn-default, .p-button');
      if (buttons.length === 0) return;

      observer.disconnect();

      buttons.forEach((btn) => {
        if (btn.classList.contains('p-dialog-header-icon') || btn.closest('.p-dialog-header-icon')) return;

        // Check if visual state or classes have changed since last process
        const hasIcon = btn.querySelector('svg') || btn.querySelector('img') || btn.querySelector('i') || btn.querySelector('.fa') || btn.querySelector('.pi') || btn.querySelector('.p-button-icon');
        const textContent = btn.textContent.trim();
        const currentFingerprint = `${btn.className}|${textContent}|${hasIcon ? 'icon' : 'no-icon'}`;

        if (btn.getAttribute('data-last-processed') === currentFingerprint) {
          return;
        }

        const hasText = textContent.length > 0;

        if (hasIcon && !hasText) {
          if (btn.getAttribute('data-icon-only') !== 'true') {
            btn.setAttribute('data-icon-only', 'true');
          }

          const nameOrClass = (btn.className + ' ' + (btn.getAttribute('title') || '') + ' ' + btn.innerHTML).toLowerCase();
          let type = 'default';
          let tooltip = '';

          if (nameOrClass.includes('delete') || nameOrClass.includes('remove') || nameOrClass.includes('trash') || nameOrClass.includes('danger')) {
            type = 'delete';
            tooltip = 'Delete';
          } else if (nameOrClass.includes('warning') || nameOrClass.includes('alert') || nameOrClass.includes('exclamation')) {
            type = 'warning';
            tooltip = 'Warning';
          } else if (nameOrClass.includes('success') || nameOrClass.includes('check') || nameOrClass.includes('approve') || nameOrClass.includes('save') || nameOrClass.includes('submit')) {
            type = 'success';
            tooltip = 'Success';
          }

          if (btn.getAttribute('data-action-type') !== type) {
            btn.setAttribute('data-action-type', type);
          }
          if (!btn.hasAttribute('title') && tooltip) {
            btn.setAttribute('title', tooltip);
          }
          btn.setAttribute('data-last-processed', currentFingerprint);
          return;
        }

        // Clean up icon-only flags if button has text
        btn.removeAttribute('data-icon-only');
        btn.removeAttribute('data-action-type');

        // Text-only buttons globally
        if (
          hasIcon ||
          btn.querySelector('.spinner-border')
        ) {
          btn.removeAttribute('data-action-icon');
          btn.setAttribute('data-last-processed', currentFingerprint);
          return;
        }

        const text = textContent.toLowerCase();
        if (!text) {
          btn.removeAttribute('data-action-icon');
          btn.setAttribute('data-last-processed', currentFingerprint);
          return;
        }

        let actionType = '';
        let tooltipText = '';

        if (
          text.includes('cancel') ||
          text.includes('close') ||
          text === 'no' ||
          text.includes('discard') ||
          text.includes('dismiss')
        ) {
          actionType = 'cancel';
          tooltipText = text.charAt(0).toUpperCase() + text.slice(1);
        } else if (
          text.includes('delete') ||
          text.includes('remove') ||
          text.includes('reject') ||
          text.includes('exclude')
        ) {
          actionType = 'delete';
          tooltipText = text.charAt(0).toUpperCase() + text.slice(1);
        } else if (text.includes('edit') || text.includes('modify') || text.includes('rename')) {
          actionType = 'edit';
          tooltipText = text.charAt(0).toUpperCase() + text.slice(1);
        } else if (text.includes('search') || text.includes('filter') || text.includes('find')) {
          actionType = 'search';
          tooltipText = text.charAt(0).toUpperCase() + text.slice(1);
        } else if (text.includes('view') || text.includes('show') || text.includes('preview') || text.includes('detail')) {
          actionType = 'view';
          tooltipText = text.charAt(0).toUpperCase() + text.slice(1);
        } else if (text.includes('reset') || text.includes('clear')) {
          actionType = 'reset';
          tooltipText = text.charAt(0).toUpperCase() + text.slice(1);
        } else if (text.includes('export') || text.includes('share')) {
          actionType = 'export';
          tooltipText = text.charAt(0).toUpperCase() + text.slice(1);
        } else if (text.includes('copy') || text.includes('duplicate')) {
          actionType = 'copy';
          tooltipText = text.charAt(0).toUpperCase() + text.slice(1);
        } else if (text.includes('send') || text.includes('publish')) {
          actionType = 'send';
          tooltipText = text.charAt(0).toUpperCase() + text.slice(1);
        } else if (text.includes('login') || text.includes('signin')) {
          actionType = 'login';
          tooltipText = 'Log In';
        } else if (text.includes('refresh') || text.includes('reload')) {
          actionType = 'refresh';
          tooltipText = 'Refresh';
        } else if (text.startsWith('+') || text.includes('add') || text.includes('create')) {
          actionType = 'add';
          tooltipText = text.charAt(0).toUpperCase() + text.slice(1);
        } else if (text === 'next' || text.includes('proceed')) {
          actionType = 'next';
          tooltipText = 'Next Step';
        } else if (text === 'back' || text === 'previous') {
          actionType = 'back';
          tooltipText = 'Go Back';
        } else if (text.includes('upload')) {
          actionType = 'upload';
          tooltipText = 'Upload File';
        } else if (text.includes('download')) {
          actionType = 'download';
          tooltipText = 'Download File';
        } else if (
          text.includes('save') ||
          text.includes('submit') ||
          text.includes('approve') ||
          text.includes('confirm') ||
          text === 'ok' ||
          text === 'yes' ||
          text.includes('update') ||
          text.includes('apply') ||
          text.includes('import') ||
          text.includes('done') ||
          text.includes('agree')
        ) {
          actionType = 'save';
          tooltipText = text.charAt(0).toUpperCase() + text.slice(1);
        }

        if (actionType) {
          if (btn.getAttribute('data-action-icon') !== actionType) {
            btn.setAttribute('data-action-icon', actionType);
          }
          if (!btn.hasAttribute('title') && tooltipText) {
            btn.setAttribute('title', tooltipText);
          }
        } else {
          btn.removeAttribute('data-action-icon');
        }
        btn.setAttribute('data-last-processed', currentFingerprint);
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class']
      });
    };

    let rafId = null;
    const scheduleProcess = () => {
      if (rafId) {
        return;
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        processButtons();
      });
    };

    const observer = new MutationObserver((mutations) => {
      let runScan = false;
      for (const mutation of mutations) {
        if (mutation.addedNodes.length > 0 || mutation.type === 'attributes' || mutation.type === 'characterData') {
          runScan = true;
          break;
        }
      }
      if (runScan) {
        scheduleProcess();
      }
    });

    processButtons();

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      observer.disconnect();
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  return (
    <>
      <PrimeReactProvider>
        <ToastProvider>
          <EmployeFilterProvider>
            <FilterProvider>
              <ThemeProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Routes>
                    {!loginSuccess && (
                      <Route path="/sign-in" name="Login Page" element={<InsightsLogin />} />
                    )}
                    <Route path="/sign-up" name="Sign Up Page" element={<ForgotPassword />} />
                    <Route path="/reset-password" name="Reset Password" element={<Resetpass />} />
                    <Route path="/request-action" name="Request Action" element={<RequestAction />} />
                    <Route
                      path="/*"
                      element={
                        myPermission?.length > 0 ? (
                          <DefaultLayout />
                        ) : (
                          noPermission && <NoPermissionFound />
                        )
                      }
                    />
                  </Routes>
                </LocalizationProvider>
              </ThemeProvider>
            </FilterProvider>
          </EmployeFilterProvider>
        </ToastProvider>
      </PrimeReactProvider>
    </>
  );
}

export default App;
